<template>
  <section class="limiter-content pa-3 pa-lg-0">
    <div class="padding-block"></div>

    <header class="chc-header">
      <span class="chc-eyebrow">
        <v-icon small color="primary">{{ isProcurator ? "mdi-briefcase-account-outline" : "mdi-account-circle-outline" }}</v-icon>
        {{ isProcurator ? "ÁREA DO PROCURADOR" : "ÁREA DO CREDOR" }}
      </span>
      <h1 class="chc-title">Certidões de <span>crédito (CHC)</span></h1>
      <p class="chc-subtitle">
        Encerrado o prazo de habilitação e divergência do caso, o crédito reconhecido em outro processo (por exemplo, numa
        reclamatória trabalhista) é apresentado pela Certidão de Habilitação de Crédito. A Administração Judicial analisa
        cada certidão e, aprovada, inclui o crédito no quadro de credores.
      </p>
    </header>

    <template v-if="user">
      <div class="chc-list-head">
        <h2>Minhas certidões</h2>
        <span v-if="requests.length">{{ requests.length }} {{ requests.length === 1 ? "certidão" : "certidões" }}</span>
        <v-spacer></v-spacer>
        <v-btn depressed color="primary" class="chc-btn" to="/certidoes-credito/nova">
          <v-icon left small>mdi-plus</v-icon> Nova CHC
        </v-btn>
      </div>

      <v-progress-linear v-if="loading" indeterminate color="darkSecondary" class="mb-4"></v-progress-linear>

      <div v-if="!loading && !requests.length" class="chc-empty">
        <div class="chc-empty__icon"><v-icon size="34" color="darkSecondary">mdi-file-certificate-outline</v-icon></div>
        <div class="chc-empty__title">Nenhuma CHC enviada</div>
        <div class="chc-empty__text">Quando você enviar uma certidão, o andamento da análise aparece aqui.</div>
      </div>

      <article v-for="request in requests" :key="request.id" class="chc-card">
        <div class="chc-card__head">
          <div class="chc-card__icon"><v-icon color="darkSecondary">mdi-file-certificate-outline</v-icon></div>
          <div class="chc-card__main">
            <div class="chc-card__title">{{ request.protocol }}<span v-if="request.client_name"> · {{ request.client_name }}</span></div>
            <div class="chc-muted">Processo {{ request.process_number }} · enviada em {{ request.created_at | defaultDate('DD/MM/YYYY') }}</div>
          </div>
          <span class="chc-pill" :class="statusOf(request).css">
            <span class="chc-pill__dot"></span>{{ statusOf(request).label }}
          </span>
        </div>

        <!-- Pedido de documentos / certidao desatualizada -->
        <div v-if="awaitsRequester(request)" class="chc-notice chc-notice--warn">
          <v-icon small color="#a16207">mdi-file-alert-outline</v-icon>
          <div class="chc-notice__body">
            <strong>{{ request.status === 'outdated' ? 'Certidão desatualizada: envie uma nova versão da CHC' : 'A Administração Judicial pediu documentos' }}</strong>
            <div v-if="details[request.id] && details[request.id].decision_reason" class="chc-message">{{ details[request.id].decision_reason }}</div>
            <v-row dense class="mt-2">
              <v-col v-for="kind in requestedKinds(request)" :key="kind" cols="12" md="6">
                <v-file-input v-model="uploads[request.id][kind]" :label="documentName(kind)" accept=".pdf,.png,.jpg,.jpeg"
                  outlined dense hide-details prepend-icon="mdi-paperclip" background-color="#fff" />
              </v-col>
            </v-row>
            <div class="text-right mt-2">
              <v-btn depressed color="primary" class="chc-btn" :loading="sendingId === request.id"
                :disabled="!hasUploads(request)" @click="sendDocuments(request)">
                <v-icon left small>mdi-upload</v-icon> Enviar documentos
              </v-btn>
            </div>
          </div>
        </div>

        <div v-if="['approved', 'rejected'].includes(request.status) && details[request.id] && details[request.id].decision_reason"
          class="chc-notice" :class="{ 'chc-notice--danger': request.status === 'rejected' }">
          <v-icon small :color="request.status === 'rejected' ? '#b42318' : '#7e55ab'">mdi-message-text-outline</v-icon>
          <div>
            <strong>Decisão da Administração Judicial</strong>
            <div class="chc-message">{{ details[request.id].decision_reason }}</div>
          </div>
        </div>

        <!-- Detalhes -->
        <div v-if="openId === request.id && details[request.id]" class="chc-detail">
          <div class="chc-detail__grid">
            <div><span class="chc-label">Órgão julgador</span>{{ details[request.id].court || '-' }}</div>
            <div><span class="chc-label">UF</span>{{ details[request.id].process_state || '-' }}</div>
            <div><span class="chc-label">Trânsito em julgado</span>{{ details[request.id].final_judgment_at | defaultDate('DD/MM/YYYY') }}</div>
            <div><span class="chc-label">Cálculo atualizado em</span>{{ details[request.id].calculation_updated_at | defaultDate('DD/MM/YYYY') }}</div>
          </div>
          <div class="chc-label mt-3">Créditos</div>
          <div v-for="credit in details[request.id].credits" :key="credit.id" class="chc-line">
            <span>{{ creditorName(details[request.id], credit) }}</span>
            <span class="chc-muted">{{ className(credit.reviewed_class || credit.informed_class) }}</span>
            <strong>{{ credit.amount | currency }}</strong>
          </div>
          <div class="chc-label mt-3">Documentos enviados</div>
          <div v-for="doc in details[request.id].documents" :key="doc.id" class="chc-line">
            <a href="#" @click.prevent="openDocument(request, doc)"><v-icon x-small color="#7e55ab">mdi-paperclip</v-icon> {{ documentName(doc.kind) }}</a>
            <span class="chc-muted">{{ doc.original_name }}</span>
            <span class="chc-muted">{{ doc.created_at | defaultDate('DD/MM/YYYY HH:mm') }}</span>
          </div>
        </div>

        <div class="chc-card__footer">
          <v-btn text rounded small color="darkSecondary" class="chc-link" :loading="loadingId === request.id" @click="toggle(request)">
            <v-icon left small>{{ openId === request.id ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            {{ openId === request.id ? 'Ocultar detalhes' : 'Ver detalhes' }}
          </v-btn>
        </div>
      </article>
    </template>

    <login-required-notice v-else />

    <v-row><whatsButton /></v-row>
    <v-row><topButton /></v-row>
  </section>
</template>

<script>
import axios from 'axios';
import topButton from '@/components/layout/GoToTopButton.vue';
import whatsButton from '@/components/layout/WhatsAppButton.vue';
import creditorTypes from '@/common/data/creditor-types.data.js';

const STATUS = {
  pending: { label: 'Aguardando análise', css: 'chc-pill--info' },
  in_analysis: { label: 'Em análise', css: 'chc-pill--info' },
  documents_requested: { label: 'Documentos solicitados', css: 'chc-pill--warn' },
  documents_updated: { label: 'Documentos enviados', css: 'chc-pill--info' },
  outdated: { label: 'Certidão desatualizada', css: 'chc-pill--warn' },
  approved: { label: 'Aprovada', css: 'chc-pill--ok' },
  rejected: { label: 'Rejeitada', css: 'chc-pill--danger' },
};
const DOCUMENT_NAMES = {
  chc: 'Certidão de Habilitação de Crédito (CHC)',
  full_process: 'Processo completo',
  calculation_decision: 'Decisão homologatória de cálculo',
  calculation_sheet: 'Planilha de cálculos',
};

// Certidoes de Habilitacao de Credito enviadas pelo credor/procurador.
export default {
  name: 'CreditCertificates',

  components: { topButton, whatsButton },

  data() {
    return {
      user: null,
      loading: false,
      requests: [],
      details: {},
      uploads: {},
      openId: null,
      loadingId: null,
      sendingId: null,
    };
  },

  computed: {
    isProcurator() {
      return !!this.user && Number(this.user.type) === 2;
    },
  },

  created() {
    if (this.$root.user) {
      this.user = this.$root.user;
      this.load();
    }
  },

  methods: {
    statusOf(request) {
      return STATUS[request.status] || { label: request.status, css: 'chc-pill--muted' };
    },

    documentName(kind) {
      return DOCUMENT_NAMES[kind] || kind;
    },

    className(id) {
      const type = creditorTypes.find((item) => Number(item.id) === Number(id));
      return type ? type.name : `Classe ${id}`;
    },

    creditorName(detail, credit) {
      const creditor = (detail.creditors || []).find((item) => Number(item.id) === Number(credit.credit_certificate_creditor_id));
      return creditor ? creditor.name : '-';
    },

    awaitsRequester(request) {
      return ['documents_requested', 'outdated'].includes(request.status);
    },

    // Desatualizada: nova versao da CHC; pedido de documentos: os que a AJ marcou.
    requestedKinds(request) {
      if (request.status === 'outdated') return ['chc'];
      return request.requested_documents && request.requested_documents.length ? request.requested_documents : ['chc'];
    },

    hasUploads(request) {
      const files = this.uploads[request.id] || {};
      return Object.values(files).some(Boolean);
    },

    async load() {
      this.loading = true;
      try {
        const result = await axios.get('/api/credit-certificates');
        this.requests = Array.isArray(result.data.content) ? result.data.content : [];
        const uploads = {};
        this.requests.forEach((request) => { uploads[request.id] = { chc: null, full_process: null, calculation_decision: null, calculation_sheet: null }; });
        this.uploads = uploads;
        // Quem precisa agir ja ve a orientacao da AJ.
        await Promise.all(this.requests.filter((request) => this.awaitsRequester(request) || ['approved', 'rejected'].includes(request.status))
          .map((request) => this.loadDetail(request)));
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 3000 });
      } finally {
        this.loading = false;
      }
    },

    async loadDetail(request) {
      const result = await axios.get(`/api/credit-certificates/${request.id}`);
      this.$set(this.details, request.id, result.data.content);
    },

    async toggle(request) {
      if (this.openId === request.id) {
        this.openId = null;
        return;
      }
      this.loadingId = request.id;
      try {
        if (!this.details[request.id]) await this.loadDetail(request);
        this.openId = request.id;
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 3000 });
      } finally {
        this.loadingId = null;
      }
    },

    async openDocument(request, doc) {
      try {
        const result = await axios.get(`/api/credit-certificates/${request.id}/documents/${doc.id}`);
        window.open(result.data.content.url, '_blank', 'noopener');
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 3000 });
      }
    },

    async sendDocuments(request) {
      const files = this.uploads[request.id] || {};
      const tooLarge = Object.values(files).find((file) => file && file.size > 20 * 1024 * 1024);
      if (tooLarge) {
        this.$dialog.notify.error(`O arquivo "${tooLarge.name}" passa de 20 MB.`, { position: 'top-right', timeout: 5000 });
        return;
      }
      const formData = new FormData();
      Object.keys(files).forEach((kind) => { if (files[kind]) formData.append(kind, files[kind]); });

      this.sendingId = request.id;
      try {
        await axios.post(`/api/credit-certificates/${request.id}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        this.$dialog.notify.success('Documentos enviados. A Administração Judicial vai retomar a análise.', { position: 'top-right', timeout: 4000 });
        this.details = {};
        this.openId = null;
        await this.load();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
      } finally {
        this.sendingId = null;
      }
    },
  },
};
</script>

<style scoped>
/* Mesmo padrao visual de "Documentos solicitados". */
.chc-header { padding: 32px 0 24px; }
.chc-eyebrow { display: flex; gap: 8px; align-items: center; font-size: 11px; font-weight: 700; letter-spacing: 0.12em; color: #766386; }
.chc-title { margin: 14px 0 10px; font-size: clamp(28px, 3.4vw, 40px); font-weight: 700; color: #25113c; letter-spacing: -0.035em; line-height: 1.2; }
.chc-title span { color: #806697; }
.chc-subtitle { margin: 0; color: #73677c; font-size: 14px; max-width: 820px; line-height: 1.6; }
.chc-muted { font-size: 13px; color: rgba(24, 0, 50, 0.55); }
.chc-label { display: block; font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #7e55ab; margin-bottom: 2px; }

.chc-list-head { display: flex; align-items: center; gap: 10px; margin: 8px 0 12px; }
.chc-list-head h2 { margin: 0; font-size: 13px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #7e55ab; }
.chc-list-head span { font-size: 12px; color: rgba(24, 0, 50, 0.5); }

.chc-card { padding: 20px 22px; border-radius: 16px; background: #fff; border: 1px solid rgba(24, 0, 50, 0.07); margin-bottom: 16px; }
.chc-card__head { display: flex; align-items: center; gap: 12px 16px; flex-wrap: wrap; }
.chc-card__icon { width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: rgba(120, 119, 229, 0.12); flex-shrink: 0; }
.chc-card__main { flex: 1 1 260px; min-width: 0; }
.chc-card__title { font-size: 15px; font-weight: 600; color: #180032; }
.chc-card__footer { display: flex; justify-content: flex-end; margin-top: 10px; padding-top: 8px; border-top: 1px solid rgba(24, 0, 50, 0.06); }

.chc-notice { display: flex; align-items: flex-start; gap: 10px; padding: 12px 16px; border-radius: 12px; margin-top: 14px; background: rgba(120, 119, 229, 0.09); color: #2a1a40; font-size: 14px; line-height: 1.55; }
.chc-notice .v-icon { margin-top: 2px; }
.chc-notice__body { flex: 1; min-width: 0; }
.chc-notice--warn { background: #fff7e6; }
.chc-notice--danger { background: #fdecea; color: #7a1b12; }
.chc-message { white-space: pre-wrap; margin-top: 2px; }

.chc-detail { margin-top: 14px; padding-top: 14px; border-top: 1px solid rgba(24, 0, 50, 0.06); font-size: 14px; color: #2a1a40; }
.chc-detail__grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; }
.chc-line { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 16px; padding: 6px 0; border-bottom: 1px dashed rgba(24, 0, 50, 0.08); }
.chc-line > :first-child { flex: 1 1 240px; }
.chc-line a { color: #7e55ab; text-decoration: none; }
.chc-line a:hover { text-decoration: underline; }

.chc-link, .chc-btn { text-transform: none; letter-spacing: 0; font-weight: 600; }
.chc-btn { height: 38px !important; padding: 0 16px !important; border-radius: 10px; }

.chc-pill { display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; border-radius: 999px; font-size: 12px; font-weight: 600; white-space: nowrap; }
.chc-pill__dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
.chc-pill--ok { background: #e7f6ee; color: #1e7b4f; }
.chc-pill--warn { background: #fff4dc; color: #a16207; }
.chc-pill--info { background: rgba(120, 119, 229, 0.12); color: #4b3a8f; }
.chc-pill--danger { background: #fdecea; color: #b42318; }
.chc-pill--muted { background: #f1f0f4; color: #6b6475; }

.chc-empty { text-align: center; padding: 40px 16px 56px; }
.chc-empty__icon { width: 72px; height: 72px; border-radius: 50%; margin: 0 auto 16px; display: flex; align-items: center; justify-content: center; background: rgba(120, 119, 229, 0.12); }
.chc-empty__title { font-size: 17px; font-weight: 600; color: #180032; }
.chc-empty__text { margin-top: 6px; color: rgba(24, 0, 50, 0.6); font-size: 14px; }
</style>
